import { EditableCallbackParams, IRowNode, ValueFormatterParams } from '@ag-grid-community/core';
import { Signal } from '@angular/core';
import { Dayjs } from 'dayjs';

import { AttributeConfig } from '@shared/components/ag-raw-attribute-renderer/ag-raw-attribute-renderer';
import { ActivityType, Currency } from '@shared/services/gql.service';
import { Maybe, Utils } from '@shared/utils/utils';

import { QuarterCloseAdjustmentsComponent } from '../quarter-close-adjustments.component';
import { ExpenseSource, QuarterCloseAdjustmentGridData } from '../quarter-close-adjustments.model';

export type InMonthAdjustmentGridConfigContext = {
  allEditableColumnsHidden: Signal<boolean>;
  attributeConfig: AttributeConfig<QuarterCloseAdjustmentGridData>;
  editableVeColumns: string[];
  editMode: Signal<boolean>;
  flags: {
    showAvailableInvoice: boolean;
    showUnitTotals: boolean;
    showUOM: boolean;
    showVendorEstimateAmount: boolean;
    showVendorEstimatePercent: boolean;
  };
  gridData: QuarterCloseAdjustmentGridData[];
  permissions: {
    editVendorEstimate: boolean;
    modifyOpenMonthAdjustments: boolean;
  };
  possibleEditColumns: Signal<{
    tmaCanBeEdited: boolean;
    veCanBeEdited: boolean;
  }>;
  qcaComponent: QuarterCloseAdjustmentsComponent;
  rows: {
    edited: Set<string>;
    historicalAdjustmentChanged: Set<string>;
    sourceChanged: Set<string>;
    totalChanged: Set<string>;
    unitChanged: Set<string>;
    vendorEstimateAmountLtdChanged: Set<{ activity_id: string; time: Dayjs }>;
    vendorEstimateChanged: Set<{ activity_id: string; time: Dayjs }>;
    vendorEstimatePercentChanged: Set<{ activity_id: string; time: Dayjs }>;
  };
  selectedMonth: string;
  updateDynamicFields: (
    rawNewValue: number,
    activityId: string,
    colDefField: string | undefined,
    pTmaUnitCost: number,
    pHistoricalAdjustmentAmount: number,
    vendor_estimate_amount: number,
    oldValue: number,
    tmaUnit: number,
    tmaAmount: number,
    totalAmount: number,
    tma_source: ExpenseSource,
    rowData: QuarterCloseAdjustmentGridData
  ) => void;
};

export const getSafeVendorCurrency = (params: ValueFormatterParams) =>
  params.data?.vendorCurrency || params.node?.aggData?.vendorCurrency || Currency.USD;

export const currencyFormatter = (params: ValueFormatterParams) => {
  return Utils.agCurrencyFormatterAccounting(params, getSafeVendorCurrency(params));
};

export type InvoiceExpenseSourceFilterType = {
  [key: `ACTIVITY_DISCOUNT_${string}`]: boolean;
  [key: `ACTIVITY_INVESTIGATOR_${string}`]: boolean;
  [key: `ACTIVITY_PASSTHROUGH_${string}`]: boolean;
  [key: `ACTIVITY_SERVICE_${string}`]: boolean;
};

export const isDiscountRow = (params: EditableCallbackParams) => {
  return params.node.data?.activity_type === ActivityType.ACTIVITY_DISCOUNT;
};

export const isTotalRow = <T extends { node: Maybe<IRowNode> }>(params: T) => {
  return !!params.node?.isRowPinned();
};

export const defaultHeaderClass = [
  'text-center',
  '[&_.ag-header-cell-text]:whitespace-normal',
  '[&_.ag-header-cell-text]:line-clamp-2',
];
export const defaultMinWidth = 56;
export const defaultWidthForMoneyColumns = 140;
